<template>
  <component
    v-if="component"
    :is="component"
    :column="column"
    :row="row"
    :value="value"
  />
  <td v-else class="table-cell" :class="{ nowrap }">
    <template v-if="multiValue">
      <span class="value" v-for="(entry, index) in value">
        <value-renderer
          :type="column.type"
          multi
          :last="index === value.length - 1"
          :value="entry"
        />
      </span>
    </template>
    <template v-else>
      <value-renderer :type="column.type" :value="value" />
    </template>
  </td>
</template>
<script>
import _get from "lodash/get";

import QuickTagCell from "./cells/QuickTagCell.vue";
import AvatarCell from "./cells/Avatar.vue";
import TimeCell from "./cells/TimeCell.vue";
import ThumbnailCell from "./cells/Thumbnail.vue";
import CurrencyCell from "./cells/Currency.vue";
import ButtonCell from "./cells/Button.vue";
import DateCell from "./cells/DateCell.vue";
import DueCell from "./cells/DueCell.vue";
import BooleanCell from "./cells/BooleanCell.vue";
import FilesizeCell from "./cells/FilesizeCell.vue";
import DurationCell from "./cells/DurationCell.vue";
import SecurityCell from "./cells/SecurityCell.vue";
import URLCell from "./cells/URLCell.vue";
import ComputedCell from "./cells/ComputedCell.vue";
import ValueRenderer from "./cells/Value.vue";
import ComplexCell from "./cells/ComplexCell.vue";
import PersonaEmailCell from "./cells/PersonaEmailCell.vue";
import PersonaInviteCell from "./cells/PersonaInviteStatusCell.vue";
import StatusCell from "./cells/StatusCell.vue";
import TitleCell from "./cells/TitleCell.vue";
import ScopeCircleCell from "./cells/ScopeCircleCell.vue";
import ContentTypeCell from "./cells/ContentTypeCell.vue";
import RosterCell from "./cells/RosterCell.vue";
import RunsheetCell from "./cells/RunsheetCell.vue";

function extract(row, path) {
  if (!row) {
    return;
  }

  if (!path) {
    return;
  }

  const split = path.split("|");
  path = split[0];
  const stubFilter = split[1];

  var pathValue = _get(row, path);
  if (pathValue) {
    if (stubFilter && Array.isArray(pathValue)) {
      return pathValue.filter(function (t) {
        const definitionKey = t?.meta?.definition;

        if (stubFilter === "null") {
          return !definitionKey;
        }

        // If there is a definition on the item
        if (definitionKey) {
          // Ensure it matches the stub filter
          return definitionKey === stubFilter;
        }
      });
    } else {
      return pathValue;
    }
  }

  ///////////////////////////////

  var output;

  const pieces = path.split(".");
  const key = pieces.shift();
  const remainder = pieces.join(".");

  const stub = _get(row, key);
  if (Array.isArray(stub)) {
    return stub
      .map(function (r) {
        return extract(r, remainder);
      })
      .flat()
      .filter(Boolean);
  } else {
    return extract(stub, remainder);
  }
}

export default {
  data() {
    return {
      component: null,
    };
  },
  components: {
    ValueRenderer,
  },
  async created() {
    const self = this;

    let component;

    ///////////////////////////////

    let cellType = self.column.type;
    let cellRenderer = self.column.renderer;
    let cellKey = self.column.key;

    if (!cellRenderer) {
      switch (cellKey) {
        case "fileSize":
          cellRenderer = "filesize";
          break;
        case "duration":
          cellRenderer = "duration";
          break;
        case "title":
          cellRenderer = "title";
          break;
        case "status":
        case "paymentStatus":
        case "meta.status":
          cellRenderer = "status";
          break;
        case "meta.tags":
          cellRenderer = "tag";
          break;
        case "meta.definition":
          cellRenderer = "type";
          break;
      }
    }

    switch (self.column.widget) {
      case "button":
        break;
      default:
        cellRenderer = cellRenderer || self.column.widget;
        break;
    }

    switch (cellType) {
      case "boolean":
        cellRenderer = "boolean";
        break;
      case "reference":
        if (self.column.referenceType === "image") {
          cellRenderer = "thumbnail";
        }
        break;
    }

    ///////////////////////////////

    switch (cellRenderer) {
      case "filesize":
        component = FilesizeCell;
        break;
      case "duration":
        component = DurationCell;
        break;
      case "button":
        component = ButtonCell;
        break;
      case "security":
        component = SecurityCell;
        break;
      case "boolean":
        component = BooleanCell;
        break;
      case "roster":
        component = RosterCell;
        break;
      case "runsheet":
        component = RunsheetCell;
        break;
      case "personainvite":
        component = PersonaInviteCell;
        break;
      case "personaemail":
        component = PersonaEmailCell;
        break;
      case "time":
      case "timefield":
        component = TimeCell;
        break;
      case "date":
      case "datetime":
        component = DateCell;
        break;
      case "due":
        component = DueCell;
        break;
      case "circles":
        component = ScopeCircleCell;
        break;
      case "title":
        component = TitleCell;
        break;
      case "type":
        component = ContentTypeCell;
        break;
      case "complex":
        component = ComplexCell;
        break;
      case "status":
        component = StatusCell;
        break;
      case "computed":
        component = ComputedCell;
        break;
      case "url":
        component = URLCell;
        break;
      case "currency":
        component = CurrencyCell;
        break;
      case "tag":
        component = QuickTagCell;
        break;
      case "avatar":
        component = AvatarCell;
        break;
      case "thumbnail":
        component = ThumbnailCell;
        break;
      default:
        switch (cellType) {
          case "date":
            component = DateCell;
            break;
          case "boolean":
            component = BooleanCell;
            break;
          case "url":
            component = URLCell;
            break;
        }
        break;
    }

    ///////////////////////////////

    self.component = component;
  },
  props: {
    column: {
      type: Object,
      required: true,
    },
    row: {
      type: Object,
      required: true,
    },
  },
  computed: {
    nowrap() {
      return this.column.wrap === false;
    },
    multiValue() {
      return Array.isArray(this.value);
    },
    renderer() {
      return this.column.renderer;
    },
    renderValue() {
      var value = this.value;

      if (value === undefined || value === null) {
        return;
      }

      return value.title || value.name || value;
    },
    value() {
      var v = extract(this.row, this.column.key);
      return v;
    },
  },
};
</script>
<style scoped lang="scss">
.value {
  display: inline-block;
  margin: 0.05em;
}

.nowrap {
  white-space: nowrap;
}
</style>
